/**
 * The tab's realtime client (R21, ADR-0010, design/07 §9): `subscribe(topic, subscriber)` for
 * every topic, whichever connection carries it — the hub (`staff:*`, `pos:register:*`, `order:*`,
 * `public:*` …) or a per-service stream of ADR-0009 (`inventory.changed:*`, `inbox`,
 * `order.tracking:*`, see `streams.ts`).
 *
 * - Ref-counted: the first subscriber of a topic adds it to its connection, the last one leaving
 *   removes it (the hub then closes after 5 s without topics).
 * - A tab hidden for more than 5 minutes closes its connections; back in front they reopen and
 *   every subscriber is told to resync (`reason: "resume"`). `offline` closes, `online` reopens
 *   and resyncs; `pagehide` (navigation / back-forward cache) closes at once.
 * - Fallback poll: while a subscriber's topic reports `fallback` (3 failures in a row, refused,
 *   switched off) it gets `onResync("poll")` every 30 s (design/07 §9.1 "Fallback") — screens keep
 *   working, only slower.
 */
import { createHubSource, type HubOptions } from "./hub";
import type { ChangeMessage } from "./sse";
import type {
  ConnectionStatus,
  ResyncReason,
  Source,
  SourceFactory,
  SourceHost,
  Subscriber,
  TokenProvider,
  TopicStatus,
} from "./types";

export interface RealtimeOptions extends HubOptions {
  fetch?: typeof fetch;
  /** Bearer of signed-in apps (admin, POS, portal); called with `force` after a refusal. */
  getToken?: TokenProvider | null;
  /** Resolves when the first connection may start (storefront: after `load`). Default: at once. */
  ready?: () => Promise<void>;
  /** Per-service streams (ADR-0009) routed behind `subscribe()` — `inventoryStream()` … */
  streams?: readonly SourceFactory[];
  /** `false` = no hub (only service streams). */
  hub?: boolean;
  /** Hidden this long → connections close. Default 5 min. */
  hiddenCloseMs?: number;
  /** Fallback poll interval. Default 30 s; 0 = no poll. */
  pollMs?: number;
  /** Tests: do not listen to document / window events. */
  watchPage?: boolean;
}

export interface RealtimeClient {
  /** Subscribe `topic`; returns the unsubscribe function. */
  subscribe(topic: string, subscriber: Subscriber): () => void;
  /** Worst state of the open connections (`idle` = nothing subscribed). */
  status(): ConnectionStatus;
  topicStatus(topic: string): TopicStatus;
  onStatus(listener: (status: ConnectionStatus) => void): () => void;
  /** Token of the tracking link — proves `order:<number>` (sent as `orderToken`). */
  setOrderToken(token: string | null): void;
  setTokenProvider(provider: TokenProvider | null): void;
  /** Call after the app refreshed its access token: the hub renews without reconnecting. */
  tokenChanged(): void;
  /** Topics the open connections carry (tests / debugging). */
  wireTopics(): string[];
  close(): void;
}

const STATUS_ORDER: readonly ConnectionStatus[] = [
  "offline",
  "fallback",
  "reconnecting",
  "connecting",
  "paused",
  "live",
];

export function createRealtimeClient(options: RealtimeOptions = {}): RealtimeClient {
  const fetcher: typeof fetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const hiddenCloseMs = options.hiddenCloseMs ?? 5 * 60_000;
  const pollMs = options.pollMs ?? 30_000;
  const watch = options.watchPage ?? true;
  let getToken: TokenProvider | null = options.getToken ?? null;
  let forceToken = false;
  let orderToken: string | null = null;
  let started = false;
  let closed = false;
  let status: ConnectionStatus = "idle";
  let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let notifyQueued = false;

  const subs = new Map<string, Set<Subscriber>>();
  const lastStatus = new WeakMap<Subscriber, TopicStatus>();
  const listeners = new Set<(status: ConnectionStatus) => void>();

  function resyncTopics(topics: Iterable<string>, reason: ResyncReason): void {
    for (const topic of [...topics]) {
      for (const subscriber of [...(subs.get(topic) ?? [])]) {
        subscriber.onResync?.(reason);
      }
    }
  }

  const sources: Source[] = [];

  /** The host of one source: its `resync(null)` means "my topics", not every topic of the tab. */
  function bind(make: (h: SourceHost) => Source): void {
    let self: Source | null = null;
    const host: SourceHost = {
      fetch: fetcher,
      change(message: ChangeMessage) {
        for (const subscriber of [...(subs.get(message.topic) ?? [])]) {
          subscriber.onChange(message);
        }
      },
      resync(topics, reason) {
        resyncTopics(topics ?? [...subs.keys()].filter((t) => sourceOf(t) === self), reason);
      },
      statusChanged,
      async token() {
        if (!getToken) {
          return null;
        }
        const force = forceToken;
        forceToken = false;
        try {
          return await getToken(force);
        } catch {
          return null;
        }
      },
      refuseToken() {
        forceToken = true;
      },
      hasTokenProvider: () => getToken !== null,
      orderToken: () => orderToken,
    };
    self = make(host);
    sources.push(self);
  }

  /** Coalesced: a source may change state several times while handling one event. */
  function statusChanged(): void {
    if (!notifyQueued) {
      notifyQueued = true;
      queueMicrotask(() => {
        notifyQueued = false;
        notify();
      });
    }
  }

  for (const factory of options.streams ?? []) {
    bind((h) => factory(h, options));
  }
  if (options.hub !== false) {
    bind((h) => createHubSource(h, options));
  }

  function sourceOf(topic: string): Source | null {
    return sources.find((s) => s.accepts(topic)) ?? null;
  }

  function topicStatus(topic: string): TopicStatus {
    const source = sourceOf(topic);
    if (!source || closed) {
      return "fallback"; // no connection can carry it: poll
    }
    const s = source.status();
    if (s === "offline" || s === "paused") {
      return "connecting";
    }
    return source.topicStatus(topic);
  }

  function computeStatus(): ConnectionStatus {
    if (subs.size === 0) {
      return "idle";
    }
    const states = new Set<ConnectionStatus>();
    for (const topic of subs.keys()) {
      const source = sourceOf(topic);
      states.add(source ? source.status() : "fallback");
      if (source && source.topicStatus(topic) === "fallback") {
        states.add("fallback");
      }
    }
    return STATUS_ORDER.find((s) => states.has(s)) ?? (started ? "connecting" : "idle");
  }

  function notify(): void {
    let anyFallback = false;
    for (const [topic, set] of subs) {
      const now = topicStatus(topic);
      anyFallback ||= now === "fallback";
      for (const subscriber of set) {
        if (lastStatus.get(subscriber) !== now) {
          lastStatus.set(subscriber, now);
          subscriber.onStatus?.(now);
        }
      }
    }
    const next = computeStatus();
    if (next !== status) {
      status = next;
      for (const listener of [...listeners]) {
        listener(next);
      }
    }
    // fallback poll (design/07 §9.1): one timer for the tab
    if (anyFallback && pollMs > 0 && pollTimer === null && !closed) {
      pollTimer = setInterval(poll, pollMs);
    } else if ((!anyFallback || closed) && pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function poll(): void {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return; // nobody looks — the resume resync refreshes it
    }
    for (const [topic, set] of subs) {
      if (topicStatus(topic) === "fallback") {
        for (const subscriber of [...set]) {
          subscriber.onResync?.("poll" satisfies ResyncReason);
        }
      }
    }
  }

  function onVisibility(): void {
    if (document.visibilityState === "hidden") {
      hiddenTimer ??= setTimeout(() => {
        hiddenTimer = null;
        if (document.visibilityState === "hidden") {
          sources.forEach((s) => {
            s.pause("hidden");
          });
        }
      }, hiddenCloseMs);
      return;
    }
    if (hiddenTimer !== null) {
      clearTimeout(hiddenTimer);
      hiddenTimer = null;
    }
    resumeAll();
  }

  /** Reopen paused connections (not while the browser is offline — `online` does it). */
  function resumeAll(): void {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return;
    }
    sources.forEach((s) => {
      s.resume();
    });
  }

  function onPageHide(): void {
    sources.forEach((s) => {
      s.pause("pagehide");
    });
  }

  function onPageShow(event: PageTransitionEvent): void {
    if (event.persisted) {
      resumeAll();
    }
  }

  function onOffline(): void {
    sources.forEach((s) => {
      s.pause("offline");
    });
  }

  function onOnline(): void {
    resumeAll();
  }

  /** A superseded tab the person comes back to (click / alt-tab, still visible) tries again. */
  function onFocus(): void {
    if (typeof document === "undefined" || document.visibilityState !== "hidden") {
      resumeAll();
    }
  }

  if (watch && typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibility);
  }
  if (watch && typeof window !== "undefined") {
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);
  }

  void (options.ready ?? (() => Promise.resolve()))().then(() => {
    if (closed) {
      return;
    }
    started = true;
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      onOffline();
    }
    for (const topic of subs.keys()) {
      sourceOf(topic)?.add(topic);
    }
    notify();
  });

  return {
    subscribe(topic, subscriber) {
      let set = subs.get(topic);
      if (!set) {
        set = new Set();
        subs.set(topic, set);
        if (started && !closed) {
          sourceOf(topic)?.add(topic);
        }
      }
      set.add(subscriber);
      const now = topicStatus(topic);
      lastStatus.set(subscriber, now);
      subscriber.onStatus?.(now);
      statusChanged();
      return () => {
        const list = subs.get(topic);
        if (!list?.delete(subscriber)) {
          return;
        }
        if (list.size === 0) {
          subs.delete(topic);
          if (started && !closed) {
            sourceOf(topic)?.remove(topic);
          }
        }
        statusChanged();
      };
    },
    status: () => status,
    topicStatus,
    onStatus(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setOrderToken(next) {
      if (next !== orderToken) {
        orderToken = next;
        sources.forEach((s) => {
          s.orderTokenChanged();
        });
      }
    },
    setTokenProvider(provider) {
      getToken = provider ?? null;
    },
    tokenChanged() {
      sources.forEach((s) => {
        s.tokenChanged();
      });
    },
    wireTopics: () => sources.flatMap((s) => s.wireTopics()),
    close() {
      closed = true;
      sources.forEach((s) => {
        s.close();
      });
      subs.clear();
      if (hiddenTimer !== null) {
        clearTimeout(hiddenTimer);
      }
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
      if (watch && typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (watch && typeof window !== "undefined") {
        window.removeEventListener("offline", onOffline);
        window.removeEventListener("online", onOnline);
        window.removeEventListener("pagehide", onPageHide);
        window.removeEventListener("pageshow", onPageShow);
        window.removeEventListener("focus", onFocus);
      }
      status = "idle";
      listeners.forEach((l) => {
        l("idle");
      });
    },
  };
}

let shared: RealtimeClient | null = null;

/** The tab's one client (browser only) — created with `options` the first time. */
export function sharedRealtime(options?: RealtimeOptions): RealtimeClient {
  shared ??= createRealtimeClient(options);
  return shared;
}

/** Tests only. */
export function resetSharedRealtime(): void {
  shared?.close();
  shared = null;
}
